import { useMemo, useState, useEffect } from 'react'

import { useI18n }            from '../../../../shared/i18n/useI18n.js'
import { useProductStrings }  from '../../../../shared/i18n/useProductStrings.js'

import {
  buildColorModeLegendItems,
  encodeColorModeLegendHoverValue,
  getColorModeFilterActive,
  getNextColorModeFiltersConfig,
  isColorModeLegendKeyActive,
  resolveColorModeHoveredLegendKey,
} from '../../../catalog/utils/colorModeLegend.js'
import { 
  COLOR_MODE,
  COLOR_MODE_REGISTRY }         from '../../../../shared/constants/colorMode.js'
import { HERO_OUTLINE }         from '../../../../shared/constants/heroOutline.js'

import { ButtonHero }   from '../ButtonHero/ButtonHero.jsx'

import { Button }       from '../../../../shared/ui/Button/Button.jsx'
import { BottomSheet }  from '../../../../shared/ui/BottomSheet/BottomSheet.jsx'
import { FiltersAlert } from '../../../../shared/ui/icons/FiltersAlert.jsx'
import { HideOverlay }  from '../../../../shared/ui/icons/HideOverlay.jsx'
import { Select }       from '../../../../shared/ui/Select/index.js'
import { ShowOverlay }  from '../../../../shared/ui/icons/ShowOverlay.jsx'

import styles from './OutlineEyeControl.module.scss'

const LEGEND_COLUMNS_BY_MODE = {
  [COLOR_MODE.ROOMS]: 5,
  [COLOR_MODE.STATUS]: 3,
  [COLOR_MODE.INSOLATION]: 6,
  [COLOR_MODE.COMPARE]: 5,
  [COLOR_MODE.CATEGORY]: 4,
}

function getLegendColumns(colorMode, itemsCount) {
  const preferred = LEGEND_COLUMNS_BY_MODE[colorMode] ?? itemsCount
  return Math.max(1, Math.min(preferred, itemsCount || 1))
}


function renderColorModeMobileLegend({
  activeFiltersConfig = null,
  activeFiltersCount = 0,
  activeProduct = null,
  availableColorModes = [],
  colorMode,
  onColorModeChange,
  defaultFiltersConfig = null,

  onLegendHover,
  onShowFilters,
  products = [],
  setActiveFiltersConfig,
  showSoldHighlightNote = false
}) {
    const { t, plural } = useI18n()
    const getProductsCategoryString = useProductStrings()
  
    const [localHoveredKey, setLocalHoveredKey] = useState(null)
  
    const modeConfig = COLOR_MODE_REGISTRY[colorMode] ?? null
    const { filterId, toFilterValue, matchesFilterValue } = modeConfig ?? {}

    const modeOptions = useMemo(
      () => (availableColorModes || Object.keys(COLOR_MODE_REGISTRY))
        .map((value) => {
          const cfg = COLOR_MODE_REGISTRY[value];
          if (!cfg) return null;
          return {
            value,
            cfg,
            label: t(cfg.labelKey),
          };
        })
        .filter(Boolean),
      [t, availableColorModes],
    );
    // TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
    const legendItems = useMemo(() => {
      return buildColorModeLegendItems(modeConfig, products)
    }, [modeConfig, products])
    
    // TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
    const modeFilterActive = getColorModeFilterActive(filterId, activeFiltersConfig, defaultFiltersConfig)
    
  
    useEffect(() => () => onLegendHover?.(null), [onLegendHover])
    
    const legendColumns = getLegendColumns(colorMode, legendItems.length)
    const showFilterAlert = activeFiltersCount > 0 && typeof onShowFilters === 'function'

    function isLegendKeyActive(key) {
      return isColorModeLegendKeyActive({
        legendKey: key,
        filterId,
        toFilterValue,
        matchesFilterValue,
        activeFiltersConfig,
        defaultFiltersConfig,
      })
    }
  
    function clearLegendHover() {
      setLocalHoveredKey(null)
      onLegendHover?.(null)
    }
  
    function handleLegendHover(key) {
      setLocalHoveredKey(key)
      onLegendHover?.(encodeColorModeLegendHoverValue(colorMode, key))
    }
  
    function handleLegendClick(legendKey) {
      if (!filterId || !toFilterValue || !setActiveFiltersConfig || !defaultFiltersConfig) return
  
      setActiveFiltersConfig((prev) => getNextColorModeFiltersConfig({
        activeFiltersConfig: prev,
        defaultFiltersConfig,
        filterId,
        toFilterValue,
        legendKey,
      }))
    }

    // TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util? -------------------------------------------------------------------------------------------------
    function getLegendLabels(item) {
      const { key, labelKey, labelFn } = item

      if (colorMode === COLOR_MODE.ROOMS) {
        return {
          displayLabel: key === 0 ? t('tiers.rooms_service') : String(key),
          fullLabel: labelFn ? labelFn(t, plural, key) : String(key),
        }
      }

      if (colorMode === COLOR_MODE.INSOLATION) {
        return {
          displayLabel: key === 'null' ? '∅' : key === 10 ? '10h' : `${key}h+`,
          fullLabel: labelKey ? t(labelKey) : String(key),
        }
      }

      if (colorMode === COLOR_MODE.COMPARE) {
        const decile = Number(String(key).replace('p', '')) + 1
        return {
          displayLabel: `${decile * 10}%`,
          fullLabel: t(`product.compare_tooltip_${key}`, `${decile * 10}%`),
        }
      }

      if (colorMode === COLOR_MODE.CATEGORY) {
        const fullLabel = getProductsCategoryString(null, key, { count: 1 })
        return {
          displayLabel: fullLabel,
          fullLabel,
        }
      }

      const fullLabel = labelFn ? labelFn(t, plural, key) : t(labelKey)
      return {
        displayLabel: fullLabel,
        fullLabel,
      }
    }

    return (
      <div className={styles.legendHolder}>
        {/* Tiers View Legend - better suited for mobile view in bottom sheet */}
              <div className={styles.labelPane}>
                <div className={styles.labelContainer}>
                  <span className={styles.legendLabel}>{t('hero.model_outline_color_scheme')}</span>
                  <Select
                    value={colorMode}
                    onChange={onColorModeChange}
                    options={modeOptions}
                    className={styles.modeSelect}
                    preferDropUp
                  />
                </div>
                {showFilterAlert && (
                  <Button
                    type="button"
                    size='medium'
                    className={styles.filterAlertButton}
                    onClick={onShowFilters}
                    aria-label={t('common.filter')}
                    title={t('common.filter')}
                  >
                    <FiltersAlert className={styles.cornerIcon} />
                    <span className={styles.filterAlertMark} aria-hidden="true">!</span>
                  </Button>
                )}
                {showSoldHighlightNote && (
                  <div className={styles.soldBadgeBox}>
                  <span className={styles.soldBadge}>
                    {t('status.sold')}
                  </span>
                  </div>
                )}
              </div>
        
              <div className={styles.items}>
              {/* <div className={styles.itemsGrid}> */}
                {legendItems.map(({ key, labelKey, labelFn, fill, stroke, hoverFill, hoverOpacity }) => {
                  const isActive = isLegendKeyActive(key)
                  const isOff     = modeFilterActive && !isActive
                  const isHighlighted = localHoveredKey === key
                  // const label     = labelFn ? labelFn(t, plural, key) : t(labelKey)
                  const { displayLabel, fullLabel } = getLegendLabels({ key, labelKey, labelFn })
        
                  // Swatch fill: off → transparent, highlighted → hoverFill, default → fill
                  const swatchBg = isOff ? 'transparent' : (isHighlighted && hoverFill ? hoverFill : fill)
        
                  return (
                    <button
                      key={key}
                      type="button"
                      className={[
                        styles.item,
                        isOff     ? styles.itemOff     : '',
                        isActive  ? styles.itemActive  : '',
                        isHighlighted ? styles.itemHighlight : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleLegendClick(key)}
                      onMouseEnter={() => handleLegendHover(key)}
                      onMouseLeave={clearLegendHover}
                      onFocus={() => handleLegendHover(key)}
                      onBlur={clearLegendHover}
                      title={fullLabel}
                    >
                      <span
                        className={styles.swatch}
                        style={{
                          backgroundColor: swatchBg,
                          borderColor: stroke,
                          opacity: isHighlighted && hoverOpacity ? hoverOpacity : undefined,
                        }}
                      />
                      <span className={styles.itemLabel}>{displayLabel}</span>
                      {/* {label} */}
                    </button>
                  )
                })}
        
                {/* Active (open) product indicator */}
                {activeProduct && (
                  <span className={`${styles.item} ${styles.itemActiveProduct}`}>
                    <span className={`${styles.swatch} ${styles.swatchActive}`} />
                    <span className={styles.itemLabel}>
                      {t('tiers.active_product')}
                    </span>
                  </span>
                )}
              </div>
      
      </div>
    )
};

export function OutlineEyeControl({
  isOutlineVisible,
  activeOutline,
  onToggleOutline,
  colorMode,
  onColorModeChange,
  availableColorModes,
  isMobile,
  buttonClassName,
  buttonActiveClassName,
  iconClassName,

              
  activeFiltersConfig,
  activeFiltersCount,
  activeProduct,
  colorModeDeciles,
  defaultFiltersConfig,
  legendContainerRef,
  onLegendHover,
  onShowFilters,
  products,
  setActiveFiltersConfig,
  showSoldHighlightNote,

}) {

  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings()

  const [isHovered, setIsHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showSheet, setShowSheet] = useState(false)
  const [isSheetMinimized, setIsSheetMinimized] = useState(false)

  const options = useMemo(
    () => Object.entries(COLOR_MODE_REGISTRY)
      .filter(([value]) => !availableColorModes || availableColorModes.includes(value))
      .map(([value, cfg]) => ({
        value,
        label: t(cfg.labelKey),
      })),
    [t, availableColorModes],
  )

  const isUnitsOutline = activeOutline === HERO_OUTLINE.APARTMENTS;
  const canShowCard = !isMobile && options.length > 0 && isUnitsOutline
  const isCardOpen = canShowCard && isExpanded

  const showOpenEyeIcon = isOutlineVisible ? !isHovered : isHovered //TODO include mobile state without :hover
  const outlineControlLabel = isOutlineVisible 
    ? getProductsCategoryString('hide_outlines', 'default') 
    : getProductsCategoryString('show_outlines', 'default')

  const buttonClass = [
    buttonClassName,
    !isOutlineVisible ? buttonActiveClassName : '',
  ].filter(Boolean).join(' ')

  const handleEyeButtonClick = () => {
    if (isMobile) return setShowSheet((show) => !show)
    onToggleOutline()
  }



  // // Sheet gesture handlers: expand/minimize/close and swipe navigation
  // const {
  //   onBodyTouchStart,
  //   onBodyTouchMove,
  //   onBodyTouchEnd,
  // } = useBottomSheetGestures({
  //   isMinimized: isSheetMinimized,
  //   onExpand,
  //   onMinimize,
  //   onClose,
  //   // onSwipePrev: onNavigatePrev,
  //   // onSwipeNext: onNavigateNext,
  //   // onBodySwipeMove: onSwipeMove,
  // })

  return (
    <>
      <div
        className={styles.eyeControlBox}
        onMouseEnter={() => {
          setIsHovered(true)
          setIsExpanded(true)
        }}
        onMouseLeave={() => {
          setIsHovered(false)
          setIsExpanded(false)
        }}
        onFocusCapture={() => setIsExpanded(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsExpanded(false)
            setIsHovered(false)
          }
        }}
      >
        <div className={styles.buttonElement}>
          <ButtonHero
            className={buttonClass}
            onClick={handleEyeButtonClick}
            aria-label={outlineControlLabel}
          >
            {showOpenEyeIcon
              ? <ShowOverlay className={iconClassName} />
              : <HideOverlay className={iconClassName} />}
          </ButtonHero>
        </div>

        {isCardOpen && (
          <div className={styles.cardGroup}>
            <div className={styles.card}>
              <span className={styles.buttonLabel}>
                {showOpenEyeIcon
                  ? <ShowOverlay />
                  : <HideOverlay />
                }
                {outlineControlLabel}
              </span>
              <span className={styles.panelLabel}>{t('hero.model_outline_color_scheme')}</span>
              <Select
                className={styles.select}
                value={colorMode}
                onChange={onColorModeChange}
                options={options}
              />
            </div>
          </div>
        )}
      </div>

      {isMobile && (
        <BottomSheet
          isOpen={showSheet}
          onClose={() => setShowSheet(false)}
          ariaLabel={t('hero.model_outline_color_scheme')}
          blocking={false}
          closeLabel={t('hero.model_outlines_dialog_close')}          
          minimizeLabel={t('common.minimize')}
          expandLabel={t('common.expand')}
          showMinimizeButton
          isMinimized={isSheetMinimized}
          onMinimize={() => setIsSheetMinimized(true)}
          onExpand={() => setIsSheetMinimized(false)}
          bottomInset="var(--bim-mobile-nav-inset-bottom)"
        >
          <BottomSheet.Header>
            <span>{t('hero.model_outlines_dialog_heading')}</span>
          </BottomSheet.Header>

          <BottomSheet.Content>
            <div className={[
              styles.contentBlock,
              isSheetMinimized ? styles.contentBlockMinimized : '',
            ].filter(Boolean).join(' ')}>
              <div className={styles.actionsHolder}>
                <div className={styles.sheetButtonBox}>
                  <Button
                    type="button"
                    kind="icon"
                    size="large"
                    onClick={onToggleOutline}
                    aria-pressed={isOutlineVisible}
                    aria-label={outlineControlLabel}
                  >
                    {showOpenEyeIcon
                      ? <HideOverlay />
                      : <ShowOverlay />
                    }
                  </Button>
                  <span className={styles.sheetButtonLabel}>
                    {outlineControlLabel}
                  </span>
                </div>
              </div>
              {
              renderColorModeMobileLegend({
                activeFiltersConfig,
                activeFiltersCount,
                activeProduct,
                availableColorModes,
                colorMode,
                colorModeDeciles,
                onColorModeChange,
                defaultFiltersConfig,
                legendContainerRef,
                onLegendHover,
                onShowFilters,
                products,
                setActiveFiltersConfig,
                showSoldHighlightNote,
              })
              }
            </div>
          </BottomSheet.Content>
        </BottomSheet>
      )}
    </>
  )
}
